import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { NARRATION_TTS_PROVIDER_LABELS } from '@/lib/narrationAudioSettings';
import {
  extractMurfLocaleOptions,
  filterMurfVoicesByLocale,
  filterVoicesBySearch,
  languageToMurfLocale,
} from '@/lib/murfVoices';

export function NarrationVoicePickerDialog({
  open,
  onOpenChange,
  provider,
  language = 'en',
  onSelectVoice,
}) {
  const [catalog, setCatalog] = useState({});
  const [murfLocales, setMurfLocales] = useState({});
  const [catalogMeta, setCatalogMeta] = useState({});
  const [loading, setLoading] = useState(false);
  const [previewingVoiceId, setPreviewingVoiceId] = useState('');
  const [murfLocale, setMurfLocale] = useState(languageToMurfLocale(language));
  const [searchQuery, setSearchQuery] = useState('');
  const catalogRef = useRef(catalog);
  catalogRef.current = catalog;

  const loadVoices = useCallback(async (prov, lang, { force = false } = {}) => {
    if (!prov || prov === 'auto') return;
    if (!force && catalogRef.current[prov]?.length) return;
    setLoading(true);
    setCatalogMeta((prev) => ({ ...prev, [prov]: { configured: true, error: null } }));
    try {
      // Full Murf catalog — locale filtering happens client-side so the dropdown stays populated.
      const { data } = await api.get(`/tts/voices/${prov}`);
      const voices = data.voices || [];
      setCatalog((prev) => ({ ...prev, [prov]: voices }));
      setCatalogMeta((prev) => ({
        ...prev,
        [prov]: {
          configured: data.configured !== false,
          error: data.error || null,
        },
      }));
      if (prov === 'murf') {
        const locales = data.locales || extractMurfLocaleOptions(voices, data.locales);
        setMurfLocales((prev) => ({ ...prev, [prov]: locales }));
        const preferred = languageToMurfLocale(lang);
        const hasPreferred = locales.some((o) => o.locale === preferred);
        setMurfLocale(hasPreferred ? preferred : (locales[0]?.locale || preferred));
      }
      if (data.configured === false) {
        toast.info(`${NARRATION_TTS_PROVIDER_LABELS[prov] || prov} is not configured on the backend`);
      } else if (data.error) {
        toast.error(data.error);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || `Could not load ${NARRATION_TTS_PROVIDER_LABELS[prov] || prov} voices`;
      setCatalogMeta((prev) => ({
        ...prev,
        [prov]: { configured: true, error: detail },
      }));
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open || !provider || provider === 'auto') return;
    loadVoices(provider, language, { force: false });
  }, [open, provider, language, loadVoices]);

  useEffect(() => {
    if (provider !== 'murf') return;
    const preferred = languageToMurfLocale(language);
    const locales = murfLocales.murf || [];
    const hasPreferred = locales.some((o) => o.locale === preferred);
    setMurfLocale(hasPreferred ? preferred : (locales[0]?.locale || preferred));
  }, [language, provider, murfLocales.murf]);

  useEffect(() => {
    if (!open) setSearchQuery('');
  }, [open]);

  const handleOpenChange = (next) => {
    onOpenChange?.(next);
  };

  const localeOptions = useMemo(() => {
    if (provider !== 'murf') return [];
    return murfLocales.murf || extractMurfLocaleOptions(catalog.murf, murfLocales.murf);
  }, [provider, catalog.murf, murfLocales.murf]);

  const voices = useMemo(() => {
    const raw = catalog[provider] || [];
    const searching = Boolean(searchQuery.trim());
    let base = raw;
    if (provider === 'murf' && !searching) {
      base = filterMurfVoicesByLocale(raw, murfLocale);
    } else if (provider === 'murf') {
      base = raw.map((v) => ({
        ...v,
        effective_locale: v.effective_locale || v.locale || murfLocale,
      }));
    }
    return filterVoicesBySearch(base, searchQuery);
  }, [catalog, provider, murfLocale, searchQuery]);

  const meta = catalogMeta[provider] || {};

  const playVoiceSample = async (voice) => {
    if (!voice?.voice_id) return;
    if (voice.preview_url) {
      new Audio(voice.preview_url).play();
      return;
    }
    setPreviewingVoiceId(voice.voice_id);
    try {
      const sampleLocale = voice.effective_locale || voice.locale || murfLocale;
      const { data } = await api.post(
        '/tts/voices/sample',
        { provider, voice_id: voice.voice_id, locale: sampleLocale },
        { responseType: 'blob' },
      );
      const sampleUrl = URL.createObjectURL(data);
      const audio = new Audio(sampleUrl);
      audio.onended = () => URL.revokeObjectURL(sampleUrl);
      await audio.play();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not play voice sample');
    } finally {
      setPreviewingVoiceId('');
    }
  };

  const handleSelect = (voice) => {
    const enriched = provider === 'murf'
      ? {
          ...voice,
          locale: voice.effective_locale || voice.locale || murfLocale,
        }
      : voice;
    onSelectVoice?.(enriched);
  };

  const emptyMessage = () => {
    if (searchQuery.trim()) {
      return `No voices match "${searchQuery.trim()}". Try name, voice id, language, or locale.`;
    }
    if (provider === 'murf') {
      if (meta.configured === false) {
        return 'Murf is not configured. Add MURF_API_KEY to backend/.env and restart the backend.';
      }
      if (meta.error) {
        return meta.error;
      }
      if ((catalog.murf || []).length && !voices.length) {
        return `No Murf voices for ${murfLocale}. Try another locale above.`;
      }
      return 'No Murf voices returned. Check MURF_API_KEY in backend/.env and restart the backend.';
    }
    return `No voices returned for ${NARRATION_TTS_PROVIDER_LABELS[provider] || provider}.`;
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[85vh] overflow-y-auto rounded-none"
        style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-secondary)' }}
      >
        <DialogHeader>
          <DialogTitle style={{ color: 'var(--text-primary)' }}>
            Choose {NARRATION_TTS_PROVIDER_LABELS[provider] || provider} voice
          </DialogTitle>
          <DialogDescription style={{ color: 'var(--text-secondary)' }}>
            {provider === 'murf'
              ? 'Pick a Murf locale, then select a voice for narration (powered by MURF_API_KEY on the server).'
              : 'Select a narrator voice for this project. Used when generating segment audio.'}
          </DialogDescription>
        </DialogHeader>

        {provider === 'murf' && (localeOptions.length > 0 || loading) ? (
          <div className="space-y-1 pb-2 border-b border-[var(--glass-border)]">
            <Label className="text-xs">Murf language / locale</Label>
            <Select
              value={murfLocale}
              onValueChange={setMurfLocale}
              disabled={loading || !localeOptions.length}
            >
              <SelectTrigger className="rounded-none border-[var(--glass-border)]" data-testid="narration-murf-locale-select">
                <SelectValue placeholder={loading ? 'Loading locales…' : 'Choose locale'} />
              </SelectTrigger>
              <SelectContent>
                {localeOptions.map((opt) => (
                  <SelectItem key={opt.locale} value={opt.locale}>
                    {opt.label}
                    {opt.count != null ? ` (${opt.count} voices)` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}

        <div className="space-y-1 pb-2 border-b border-[var(--glass-border)]">
          <Label className="text-xs" htmlFor="narration-voice-search">
            Search voices
          </Label>
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 pointer-events-none"
              style={{ color: 'var(--text-tertiary)' }}
            />
            <Input
              id="narration-voice-search"
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Name, voice id, language, locale, gender…"
              className="pl-9 rounded-none border-[var(--glass-border)]"
              data-testid="narration-voice-search"
              disabled={loading}
            />
          </div>
          {searchQuery.trim() && provider === 'murf' ? (
            <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
              Searching all Murf voices (locale filter paused while searching).
            </p>
          ) : null}
        </div>

        <div className="space-y-3 pr-1">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-sm" style={{ color: 'var(--text-secondary)' }}>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading voices…
            </div>
          ) : voices.length ? (
            voices.map((voice) => (
              <div
                key={voice.voice_id}
                className="border p-3 rounded-none"
                style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-tertiary)' }}
              >
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                      {voice.name || voice.voice_id}
                    </p>
                    <p className="text-xs font-mono truncate" style={{ color: 'var(--text-tertiary)' }}>
                      {voice.voice_id}
                    </p>
                    <p className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                      {voice.display_language || voice.locale || voice.language || 'Unknown'}
                      {voice.gender ? ` · ${voice.gender}` : ''}
                      {voice.accent ? ` · ${voice.accent}` : ''}
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="dashboard-btn h-8"
                      onClick={() => playVoiceSample(voice)}
                    >
                      {previewingVoiceId === voice.voice_id ? 'Playing…' : 'Sample'}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      className="dashboard-btn h-8"
                      onClick={() => handleSelect(voice)}
                      data-testid={`narration-voice-select-${voice.voice_id}`}
                    >
                      Select
                    </Button>
                  </div>
                </div>
                {voice.preview_url ? (
                  <audio controls src={voice.preview_url} className="mt-2 h-9 w-full" />
                ) : null}
              </div>
            ))
          ) : (
            <div className="text-center py-8 space-y-3">
              <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
                {emptyMessage()}
              </p>
              {provider && provider !== 'auto' ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="dashboard-btn"
                  onClick={() => loadVoices(provider, language, { force: true })}
                  data-testid="narration-voice-reload-btn"
                >
                  Retry loading voices
                </Button>
              ) : null}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
